/**
 * Helpers for `building_opening_hour` and `building_closing_hour` on address details.
 *
 * These columns store a single integer per time. Two formats are supported:
 *
 * | Stored value | Meaning   | Notes                          |
 * |--------------|-----------|--------------------------------|
 * | 8            | 8:00 AM   | Legacy whole-hour format       |
 * | 830          | 8:30 AM   | HHMM format (hour * 100 + min) |
 * | 10           | 10:00 AM  | Legacy whole-hour format       |
 * | 1030         | 10:30 AM  | HHMM format                    |
 *
 * Rule of thumb:
 * - Values below 100 are treated as a whole hour (minutes = 0).
 * - Values 100+ are read as HHMM (e.g. 830 → hour 8, minute 30).
 * - When saving, use `encode` so half-hours become HHMM and on-the-hour times
 *   below 100 stay in the legacy format.
 *
 * Typical usage:
 * - Dropdown options: `buildBuildingRestrictionHourOptions(6, 19)`
 * - Display: `formatBuildingRestrictionTime(address.building_opening_hour)`
 * - Card range: `formatBuildingRestrictionRange(opening, closing)`
 * - Compare two times: `buildingRestrictionTimeToMinutes(a) < buildingRestrictionTimeToMinutes(b)`
 */

const LEGACY_HOUR_THRESHOLD = 100;

/** Split a stored value into 24-hour clock parts. */
export const decodeBuildingRestrictionTime = (value: number): [hour: number, minute: number] => {
  if (value < LEGACY_HOUR_THRESHOLD) {
    return [value, 0];
  }

  return [Math.floor(value / 100), value % 100];
};

/** Combine hour and minute into the integer stored on address details. */
export const encodeBuildingRestrictionTime = (hour: number, minute: number): number => {
  if (minute === 0 && hour < LEGACY_HOUR_THRESHOLD) {
    return hour;
  }

  return hour * 100 + minute;
};

/** Compare two stored values (e.g. earliest must be before latest). */
export const buildingRestrictionTimeToMinutes = (value: number): number => {
  const [hour, minute] = decodeBuildingRestrictionTime(value);

  return hour * 60 + minute;
};

/** Human-readable label for admin/portal dropdowns and previews. */
export const formatBuildingRestrictionTime = (value: number): string => {
  const [hour, minute] = decodeBuildingRestrictionTime(value);
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour >= 12 ? 'PM' : 'AM';

  if (minute === 0) {
    return `${displayHour} ${period}`;
  }

  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

/**
 * Compact start/end label for dispatcher cards.
 * Includes only sides that exist so a missing hour never renders as a dangling dash.
 */
export const formatBuildingRestrictionRange = (
  openingHour?: number | null,
  closingHour?: number | null,
): string | undefined => {
  const start = typeof openingHour === 'number' ? formatBuildingRestrictionTime(openingHour) : undefined;
  const end = typeof closingHour === 'number' ? formatBuildingRestrictionTime(closingHour) : undefined;

  if (start && end) {
    return `${start} – ${end}`;
  }

  return start ?? end;
};

/** Build select options from `startHour` through `endHour` at :00 and :30. */
export const buildBuildingRestrictionHourOptions = (
  startHour: number,
  endHour: number,
): Array<{ name: string; value: number }> => {
  const options: Array<{ name: string; value: number }> = [];

  for (let hour = startHour; hour <= endHour; hour += 1) {
    for (const minute of [0, 30]) {
      const value = encodeBuildingRestrictionTime(hour, minute);

      options.push({ name: formatBuildingRestrictionTime(value), value });
    }
  }

  return options;
};
